import VideoAttribution, { videoPath } from "@/components/VideoAttribution";
import LiveDemoBadge from "@/components/LiveDemoBadge";
import MediaImage from "@/components/MediaImage";
import { explicitDemoMedia, isDemoMedia } from "@/lib/media";
import { Link } from "react-router-dom";
import { Zap } from "lucide-react";
import PlatformBadge from "@/components/PlatformBadge";
import { formatNumber, timeAgo } from "@/lib/format";

export default function DigestPreview({ digest }) {
  if (digest.total_new === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground text-sm" data-testid="digest-empty">
        Aucun nouvel outlier dans les dernières {digest.window_hours} heures pour les paramètres actuels.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="digest-list">
      {digest.top && (
        <Link to={videoPath(digest.top)} className="block rounded-xl bg-foreground text-background p-4 hover:opacity-95 transition-opacity" data-testid="digest-hero">
          <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--emerald))] font-bold">Breakout of the day</div>
          <div className="font-display text-lg font-extrabold leading-snug mt-1">{digest.top.title} <LiveDemoBadge video={digest.top} testid="digest-hero-origin" /></div>
          <VideoAttribution video={digest.top} />
          <div className="text-xs opacity-75 mt-1">{digest.top.creator_handle} · {formatNumber(digest.top.views)} views · <span className="text-[hsl(var(--emerald))] font-bold">{digest.top.outlier_multiplier.toFixed(1)}x</span> baseline</div>
        </Link>
      )}
      {digest.creators.map((c) => (
        <div key={c.creator_id} className="rounded-lg border border-border p-3" data-testid={`digest-creator-${c.creator_id}`}>
          <div className="flex items-center gap-2 mb-2">
            <MediaImage src={c.creator_avatar} alt="" name={c.creator_name || c.creator_handle} kind="avatar" demo={explicitDemoMedia(c)} className="h-7 w-7 rounded-full object-cover" />
            <Link to={`/creators/${c.creator_id}`} className="text-sm font-semibold hover:underline">{c.creator_name}</Link>
            <span className="text-xs text-muted-foreground">{c.creator_handle}</span>
            <PlatformBadge platform={c.platform} size="xs" />
            <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-[hsl(var(--emerald))]"><Zap className="h-3 w-3" />{c.count} new</span>
          </div>
          <div className="space-y-1.5">
            {c.videos.slice(0, 3).map((v) => (
              <Link key={v.id} to={videoPath(v)} className="flex items-center gap-3 rounded-md bg-muted/40 px-2 py-1.5 hover:bg-muted transition-colors">
                {/* alerts.LITE carries url, so its absence there is a Demo row; a payload without the key stays neutral. */}
                <MediaImage src={v.thumbnail} alt="" demo={"url" in v ? isDemoMedia(v) : explicitDemoMedia(v)} showLabel={false} className="h-10 w-8 rounded object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{v.title} <LiveDemoBadge video={v} testid={`digest-video-origin-${v.id}`} /></div>
                  <VideoAttribution video={v} />
                  <div className="text-[10px] text-muted-foreground">{formatNumber(v.views)} views · {v.engagement_rate}% eng · {timeAgo(v.published_at)}</div>
                </div>
                <div className="font-mono text-xs font-bold text-[hsl(var(--emerald))]">{v.outlier_multiplier.toFixed(1)}x</div>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
